import type { FilterState } from '@supply-mover-2-0/core';
import React from 'react';
import { Text } from '../components/base/Text';
import { useThemeColors } from '../providers/ThemeProvider';
import type { WidgetConfig } from './types/widget.types';

/**
 * Optional helper that renders a single widget body via a consumer-
 * supplied `loader` function. Originally hard-coded to import
 * `ReportWidgetLoader`; now the consumer (CRM dashboard, storefront
 * homepage) passes the appropriate loader so the same `WidgetGrid`
 * can render either report widgets or promo widgets without
 * duplicating the grid mechanics.
 *
 * Most consumers will skip this helper entirely and pass their own
 * `renderWidget(widget, data, ctx)` slot directly to `<WidgetGrid>`.
 * Kept around because the CRM dashboard's existing call sites
 * already use it as a memo wrapper.
 */
const deepEqual = (obj1: any, obj2: any): boolean => {
	if (obj1 === obj2) return true;
	if (obj1 == null || obj2 == null) return false;
	if (typeof obj1 !== 'object' || typeof obj2 !== 'object')
		return obj1 === obj2;

	const keys1 = Object.keys(obj1);
	const keys2 = Object.keys(obj2);

	if (keys1.length !== keys2.length) return false;

	for (const key of keys1) {
		if (!keys2.includes(key)) return false;
		if (!deepEqual(obj1[key], obj2[key])) return false;
	}

	return true;
};

interface WidgetRendererProps {
	widget: WidgetConfig;
	data: any;
	/** Refresh key to trigger data reload */
	refreshKey?: number;
	/** Refresh trigger to force data reload */
	refreshTrigger?: number;
	/** Dashboard-level filters */
	dashboardFilters?: FilterState;
	/** Callback when widget filters change */
	onFiltersChange?: (filters: FilterState) => void;
	/** Callback to report category counts from report components */
	onCategoryCountsUpdate?: (counts: Record<string, number>) => void;
	/**
	 * Loader function supplied by the consumer. CRM passes one that
	 * delegates to `ReportWidgetLoader`; storefront passes one that
	 * maps `widget.type` → promo widget components.
	 */
	loader: (props: {
		widget: WidgetConfig;
		data: any;
		refreshKey?: number;
		refreshTrigger?: number;
		dashboardFilters?: FilterState;
		widgetOverrides: Record<string, any>;
		widgetFilterConfig?: WidgetConfig['filterConfig'];
		onFiltersChanged?: (filters: FilterState) => void;
		onCategoryCountsUpdate?: (counts: Record<string, number>) => void;
	}) => React.ReactNode;
}

function WidgetRendererBase({
	widget,
	data,
	refreshKey,
	refreshTrigger,
	dashboardFilters,
	onFiltersChange,
	onCategoryCountsUpdate,
	loader,
}: WidgetRendererProps) {
	const colors = useThemeColors();

	// Get widget filter overrides from widget config
	const widgetOverrides = widget.filterConfig?.overrides || {};

	// Handle case where no data is provided
	if (!data) {
		return (
			<div className="flex-1 flex justify-center items-center">
				<Text className={`text-sm ${colors.classes.textSecondary}`}>
					Loading...
				</Text>
			</div>
		);
	}

	return (
		<>
			{loader({
				widget,
				data,
				refreshKey,
				refreshTrigger,
				dashboardFilters,
				widgetOverrides,
				widgetFilterConfig: widget.filterConfig,
				onFiltersChanged: onFiltersChange,
				onCategoryCountsUpdate,
			})}
		</>
	);
}

// Memoize to prevent unnecessary re-renders during drag
export const WidgetRenderer = React.memo(
	WidgetRendererBase,
	(prevProps, nextProps) => {
		const widgetChanged =
			prevProps.widget.id !== nextProps.widget.id ||
			prevProps.widget.type !== nextProps.widget.type;

		const dashboardFiltersChanged = !deepEqual(
			prevProps.dashboardFilters,
			nextProps.dashboardFilters,
		);

		const widgetFilterOverridesChanged = !deepEqual(
			prevProps.widget.filterConfig?.overrides,
			nextProps.widget.filterConfig?.overrides,
		);

		const prevDataWithoutRefreshKey = prevProps.data
			? { ...prevProps.data, refreshKey: undefined }
			: prevProps.data;
		const nextDataWithoutRefreshKey = nextProps.data
			? { ...nextProps.data, refreshKey: undefined }
			: nextProps.data;
		const dataChanged = !deepEqual(
			prevDataWithoutRefreshKey,
			nextDataWithoutRefreshKey,
		);

		const refreshKeyChanged =
			prevProps.data?.refreshKey !== nextProps.data?.refreshKey;

		// Loader reference change → re-render so the new loader is used.
		const loaderChanged = prevProps.loader !== nextProps.loader;

		if (
			widgetChanged ||
			dashboardFiltersChanged ||
			widgetFilterOverridesChanged ||
			dataChanged ||
			refreshKeyChanged ||
			loaderChanged
		) {
			return false;
		}

		return true;
	},
);
